import { useState } from 'react';
import React from 'react';
import { Registration } from '../API';
import { generateAttendeeCsv, downloadCsv } from '../utils/csv';

export function useAttendeeSelection(
  attendees: Registration[],
  setAttendees: React.Dispatch<React.SetStateAction<Registration[]>>,
  filteredAttendees: Array<{ registrationId: string }>,
  onCheckIn: (count: number, attendee: Registration) => Promise<void>,
) {
  const [selectedAttendees, setSelectedAttendees] = useState<Set<string>>(new Set());
  const [bulkAction, setBulkAction] = useState('');
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [selectedDownloadFormat, setSelectedDownloadFormat] = useState('csv');

  const toggleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = filteredAttendees.map(a => a.registrationId);
      setSelectedAttendees(new Set(allIds));
    } else {
      setSelectedAttendees(new Set());
    }
  };

  const toggleSelectOne = (id: string) => {
    const newSet = new Set(selectedAttendees);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setSelectedAttendees(newSet);
  };

  const handleBulkSubmit = async () => {
    const attendeesArray = Array.from(selectedAttendees);
    if (bulkAction === 'CHECK_IN') {
      const confirmed = window.confirm(`You are about to check in ${attendeesArray.length} attendee(s). Confirm?`);
      if (!confirmed) return;
      const promises = attendeesArray.map(async id => {
        const attendee = attendees.find(a => a.registrationId === id);
        if (attendee) {
          const guests = attendee.guests && attendee.guests > 0 ? attendee.guests : 1;
          return onCheckIn(guests, attendee);
        }
      });
      await Promise.all(promises);
    } else if (bulkAction === 'UNDO_CHECK_IN') {
      const confirmed = window.confirm(`You are about to undo check-in for ${attendeesArray.length} attendee(s). Confirm?`);
      if (!confirmed) return;
      const promises = attendeesArray.map(async id => {
        const attendee = attendees.find(a => a.registrationId === id);
        if (attendee) return onCheckIn(0, attendee);
      });
      await Promise.all(promises);
    } else if (bulkAction === 'SUBMIT_DATA') {
      alert('Data upload successful. Selected attendees have been marked to be submitted to CRM by end of day.');
      setAttendees(prev =>
        prev.map(attendee =>
          selectedAttendees.has(attendee.registrationId)
            ? { ...attendee, submittedToCRM: true }
            : attendee
        )
      );
    } else if (bulkAction === 'DOWNLOAD') {
      setShowDownloadModal(true);
    }
  };

  const handleDownloadConfirm = () => {
    if (selectedDownloadFormat === 'csv') {
      const selectedData = attendees.filter(att => selectedAttendees.has(att.registrationId));
      if (selectedData.length === 0) {
        alert('No attendees selected for download.');
        setShowDownloadModal(false);
        return;
      }
      const csvContent = generateAttendeeCsv(selectedData);
      downloadCsv(csvContent, 'attendees.csv');
    } else if (selectedDownloadFormat === 'excel') {
      alert('Excel download is not yet implemented.');
    } else {
      alert('Invalid download format specified.');
    }
    setShowDownloadModal(false);
  };

  return {
    selectedAttendees,
    bulkAction, setBulkAction,
    showDownloadModal, setShowDownloadModal,
    selectedDownloadFormat, setSelectedDownloadFormat,
    toggleSelectAll,
    toggleSelectOne,
    handleBulkSubmit,
    handleDownloadConfirm,
  };
}
